import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as Clock } from '@pkg/assets/icons/v2/clock.svg';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { handleKeyDown } from '@pkg/utils';

import { type EmojiId, TransferStatus } from '@app/client';
import { setTransferData, setTransferPanelOpen } from '@app/store/actions/transfers';

import '../YatTransferSheet.scss';

export interface TransferPendingProps {
    eid: EmojiId;
}

export function TransferPending({ eid }: TransferPendingProps) {
    const [translate] = useTranslation(['transfers', 'translation']);
    const { transferData } = useAppSelector((state) => state.transfers);
    const [recipientEmail, setRecipientEmail] = useState<string>();

    const dispatch = useAppDispatch();

    useEffect(() => {
        const email = transferData[eid]?.recipient_email;
        if (email && typeof email !== 'undefined') {
            setRecipientEmail(email);
        }
    }, [eid, transferData]);

    const renderCopy = useMemo(() => {
        return (
            <Trans
                i18nKey="transfers:transfer_pending_body_copy"
                ns="transfers"
                defaults="This {{yat}} has been transferred to <bold>{{transfer_email}}</bold>. Once they accept the {{yat}} we will remove it from your account."
                values={{ yat: translate('translation:yat'), transfer_email: recipientEmail }}
                components={{ bold: <strong /> }}
            />
        );
    }, [recipientEmail, translate]);

    const handleCancel = useCallback(() => {
        dispatch(setTransferData(eid, { status: TransferStatus.CancelInitiated }));
    }, [dispatch, eid]);

    return (
        <div className="yat-transfer-sidebar__step">
            <div className="yat-transfer-sidebar__flex-item">
                <div className="yat-transfer-sidebar__title-holder">
                    <h1 className="yat-transfer-sidebar__title">{translate('transfer_pending_title')} </h1>
                    <Clock className="yat-transfer-sidebar__title-icon" />
                </div>

                <p className="yat-transfer-sidebar__copy v2-text-fluid">{renderCopy}</p>
            </div>

            <div className="yat-transfer-sidebar__flex-item">
                <DepButton
                    disabled={false}
                    variant="primary"
                    display={DepButtonDisplay.minWidth}
                    size={'large'}
                    onClick={() => dispatch(setTransferPanelOpen(false))}
                >
                    {translate('transfer_pending_cta_copy')}
                </DepButton>

                <div
                    tabIndex={0}
                    className="yat-transfer-sidebar__link"
                    onClick={handleCancel}
                    aria-label={translate('cancel_transfer_cta_copy')}
                    onKeyDown={(e) => handleKeyDown(e, ['Enter', ' '], handleCancel)}
                >
                    {translate('cancel_transfer_cta_copy')}
                </div>
            </div>
        </div>
    );
}
